import { cn } from '@shared/lib/utils';
import { ComponentType, ReactNode } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  XAxis,
  XAxisProps,
  YAxis,
  YAxisProps,
} from 'recharts';
import { DataKey, Margin } from 'recharts/types/util/types';
import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from '../ui/chart';
import { CustomTooltipProps } from './tooltips/types';

interface AreaChartProps<T extends string, U extends string> {
  nameKey: T;
  dataKey: U;
  chartConfig: ChartConfig;
  chartData: any;
  legend?: boolean;
  className?: string;
  layout?: 'horizontal' | 'vertical';
  margin?: Margin;
  YAxisProps?: YAxisProps;
  XAxisProps?: XAxisProps;
  MaxBarSize?: number;
  barCategoryGap?: number | string;
  barSize?: number;
  tooltipFormatter?: ComponentType<CustomTooltipProps>;
  tooltipValueFormatter?: (value: number | string) => ReactNode;
  stacked?: boolean;
  /** Reverse tooltip row order so it matches a stacked chart's top-to-bottom visual order. */
  reverseTooltipItems?: boolean;
  /** Fades each series' fill from its colour to transparent. */
  gradientFill?: boolean;
  /**
   * Solid fill opacity applied to each Area when `gradientFill` is off.
   * Set to a low value (e.g. 0.15–0.25) for a subtle translucent band
   * under the line, or to 0 for line-only. Defaults to Recharts' own
   * default (around 0.6) when omitted.
   */
  fillOpacity?: number;
  /** Draws a hollow marker at each data point. */
  dot?: boolean;
  /**
   * Config keys rendered as a fill-less dashed line rather than a filled area —
   * for a comparison series such as the previous period. Dashed series never
   * stack and carry no dots; null months gap rather than bridge, so sparse
   * comparison data can't draw fabricated straight lines across the chart.
   */
  dashedKeys?: string[];
  strokeWidth?: number;
  areaLabel?: (props: {
    x: number;
    y: number;
    value: number | string | undefined;
    index: number;
  }) => ReactNode;
}

export default function SimpleAreaChart<
  T extends DataKey<string>,
  U extends DataKey<string[]>,
>({
  nameKey,
  chartData,
  chartConfig,
  legend,
  className,
  margin = {
    top: 10,
    right: 10,
    bottom: 10,
    left: 10,
  },
  YAxisProps,
  XAxisProps,
  tooltipFormatter: Component,
  tooltipValueFormatter,
  stacked,
  reverseTooltipItems = false,
  gradientFill = false,
  fillOpacity,
  dot = false,
  dashedKeys,
  strokeWidth = 1,
  areaLabel,
}: AreaChartProps<T, U>) {
  return (
    <ChartContainer
      config={chartConfig}
      className={(cn('aspect-auto h-full w-full'), className)}
    >
      <AreaChart data={chartData} margin={margin}>
        {gradientFill && (
          <defs>
            {Object.keys(chartConfig).map((key) => (
              <linearGradient
                key={key}
                id={`area-fill-${key}`}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop
                  offset="0%"
                  stopColor={chartConfig[key].color}
                  stopOpacity={0.18}
                />
                <stop
                  offset="100%"
                  stopColor={chartConfig[key].color}
                  stopOpacity={0}
                />
              </linearGradient>
            ))}
          </defs>
        )}
        <CartesianGrid vertical={false} stroke="var(--border)" />
        <XAxis
          dataKey={nameKey}
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          {...XAxisProps}
        />
        <YAxis
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          tickFormatter={(value) => value}
          {...YAxisProps}
        />
        <ChartTooltip
          cursor={false}
          content={
            <ChartTooltipContent
              indicator="dot"
              reverseItems={reverseTooltipItems}
              valueFormatter={tooltipValueFormatter}
              formatter={
                Component
                  ? (value, name, item, index) => (
                      <Component
                        value={value}
                        name={name}
                        item={item}
                        index={index}
                        chartConfig={chartConfig}
                      />
                    )
                  : undefined
              }
            />
          }
        />
        {legend && (
          <ChartLegend
            content={<ChartLegendContent className="text-md" />}
            verticalAlign="top"
          />
        )}
        {Object.keys(chartConfig).map((key) => {
          const isDashed = dashedKeys?.includes(key);
          return (
            <Area
              dataKey={key}
              key={key}
              stackId={stacked && !isDashed ? 'a' : key}
              fill={
                isDashed
                  ? 'none'
                  : gradientFill
                    ? `url(#area-fill-${key})`
                    : chartConfig[key].color
              }
              fillOpacity={isDashed ? 0 : fillOpacity}
              stroke={chartConfig[key].color}
              strokeWidth={strokeWidth}
              strokeDasharray={isDashed ? '5 4' : undefined}
              type="monotoneX"
              isAnimationActive={false}
              dot={
                !isDashed && dot
                  ? { r: 3, fill: '#fff', strokeWidth: 2 }
                  : false
              }
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              label={areaLabel as any}
            />
          );
        })}
      </AreaChart>
    </ChartContainer>
  );
}
